import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import {
  getMenuItemsAll, addMenuItem, deleteMenuItem, getRestaurant, updateMenuItem,
  updateItemAvailability, getItemSchedules, createItemSchedule, deleteItemSchedule,
  getTemplates, applyTemplate,
  renameSection, deleteSection, updateSectionOrder,
  translateMenuItem, translateAllMenu, saveMenuItemTranslations,
  getMenuItemTranslations,
  getMenuItemAddons, createAddon, updateAddon, deleteAddon,
  getCurrencies, updateRestaurant,
  API_ORIGIN
} from '../services/api';
import AddonForm from '../components/AddonForm';
import MenuItemsList from '../components/Admin/MenuItemsList';
import AddMenuItemForm from '../components/Admin/AddMenuItemForm';
import EditMenuItemModal from '../components/Admin/EditMenuItemModal';
import SectionsTab from '../components/Admin/SectionsTab';
import SettingsTab from '../components/Admin/SettingsTab';
import ScheduleModal from '../components/Admin/ScheduleModal';
import BulkTranslateDialog from '../components/Admin/BulkTranslateDialog';
import AddonManagementModal from '../components/Admin/AddonManagementModal';
import useMenuItemForm from '../hooks/useMenuItemForm';
import useTranslationForm from '../hooks/useTranslationForm';
import useAddonManager from '../hooks/useAddonManager';
import AppShell from '../components/layout/AppShell';
import './Admin.css';

const Admin = () => {
  const { t } = useTranslation();
  const { user, restaurants, isAdmin, isManager, isVerified } = useAuth();
  const [searchParams] = useSearchParams();
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');
  // Slug for the customer-facing menu link. `loadedSlug` tracks the saved value
  // so we only send `slug` to the API when the admin actually changed it.
  const [restaurantSlug, setRestaurantSlug] = useState('');
  const [loadedSlug, setLoadedSlug] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [showSectionOrder, setShowSectionOrder] = useState(false);
  const [sectionOrder, setSectionOrder] = useState([]);
  const [activeAdminTab, setActiveAdminTab] = useState('items');
  // Menu item form state (add + edit) - extracted to custom hook
  const {
    itemName, setItemName, itemDescription, setItemDescription,
    itemPrice, setItemPrice, itemSection, setItemSection,
    itemCategory, setItemCategory, itemImage, setItemImage,
    imagePreview, setImagePreview,
    editingItem, setEditingItem, editItemName, setEditItemName,
    editItemDescription, setEditItemDescription, editItemPrice, setEditItemPrice,
    editItemSection, setEditItemSection, editItemCategory, setEditItemCategory,
    editItemImage, setEditItemImage, editImagePreview, setEditImagePreview,
    editTranslations, setEditTranslations,
    editActiveLangTab, setEditActiveLangTab,
    editTranslating, setEditTranslating,
    updateEditTranslation, setEditTranslationsFromArray,
    resetAddForm, resetEditForm, populateEditForm,
    handleImageChange, handleEditImageChange,
  } = useMenuItemForm();

  // Translation state - extracted to custom hook
  const {
    SUPPORTED_LANGUAGES,
    baseLang, setBaseLang, activeTab, setActiveTab,
    translations, setTranslations,
    translating, setTranslating,
    bulkTranslateOpen, setBulkTranslateOpen,
    bulkTranslating, setBulkTranslating,
    bulkProgress, setBulkProgress,
    bulkResults, setBulkResults,
    updateTranslation, resetTranslations,
  } = useTranslationForm(itemName, itemDescription);

  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultDialogMessage, setResultDialogMessage] = useState('');
  const [deleteItemConfirmOpen, setDeleteItemConfirmOpen] = useState(false);
  const [deleteItemConfirmId, setDeleteItemConfirmId] = useState(null);
  const [availabilityTogglingId, setAvailabilityTogglingId] = useState(null);

  // Load from template
  const [menuTemplates, setMenuTemplates] = useState([]);
  const [selectedTemplateToApply, setSelectedTemplateToApply] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Section management
  const [newSectionName, setNewSectionName] = useState('');
  const [sectionRenameDialogOpen, setSectionRenameDialogOpen] = useState(false);
  const [sectionRenameOldName, setSectionRenameOldName] = useState('');
  const [sectionRenameNewName, setSectionRenameNewName] = useState('');
  const [sectionDeleteConfirmOpen, setSectionDeleteConfirmOpen] = useState(false);
  const [sectionDeleteConfirmName, setSectionDeleteConfirmName] = useState('');

  // Addon management - extracted to custom hook
  const {
    addonModalItem, setAddonModalItem,
    addonsList, setAddonsList,
    addonsLoading, setAddonsLoading,
    addonFormOpen, setAddonFormOpen,
    addonFormEditing, setAddonFormEditing,
    addonDeleteConfirmOpen, setAddonDeleteConfirmOpen,
    addonDeleteConfirmId, setAddonDeleteConfirmId,
    closeAddonModal, confirmDeleteAddon, cancelDeleteAddon,
  } = useAddonManager();

  // Currency / Restaurant settings
  const [currencies, setCurrencies] = useState([]);
  const [restaurantCurrency, setRestaurantCurrency] = useState({ code: 'USD', symbol: '$', position: 'before' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [taxConfig, setTaxConfig] = useState({
    tax_enabled: false,
    tax_rate: 0,
    tax_inclusive: true,
    tax_label: 'VAT',
    tax_number: '',
  });
  const [requireCustomerOtp, setRequireCustomerOtp] = useState(false);

  // Schedule modal (time-based availability)
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ name: '', dayOfWeek: 'all', startTime: '', endTime: '' });
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [outOfStockReason, setOutOfStockReason] = useState({}); // itemId -> reason (local edit)

  useEffect(() => {
    // Check URL parameter first, then set default restaurant
    const restaurantParam = searchParams.get('restaurant');
    
    if (restaurantParam) {
      const paramId = parseInt(restaurantParam);
      
      // For admins, try to find in context first, but also allow fetching directly
      if (isAdmin()) {
        const restaurant = restaurants.find(r => r.id === paramId);
        if (restaurant) {
          setRestaurantId(paramId);
          setRestaurantName(restaurant.name);
          return;
        } else {
          // Admin can access any restaurant, so fetch it directly
          setRestaurantId(paramId);
          // Name will be fetched in fetchRestaurantInfo
          return;
        }
      } else {
        // For managers, only allow restaurants they're assigned to
        const restaurant = restaurants.find(r => r.id === paramId);
        if (restaurant) {
          setRestaurantId(paramId);
          setRestaurantName(restaurant.name);
          return;
        }
      }
    }
    
    // Set default restaurant when restaurants are loaded (if no URL param or param not found)
    if (restaurants.length > 0 && !restaurantId) {
      // Prefer non-demo restaurant (id !== 1) as default
      const nonDemoRestaurant = restaurants.find(r => r.id !== 1);
      const defaultRestaurant = nonDemoRestaurant || restaurants[0];
      setRestaurantId(defaultRestaurant.id);
      setRestaurantName(defaultRestaurant.name);
    }
  }, [restaurants, searchParams, isAdmin]);

  useEffect(() => {
    if (restaurantId) {
      fetchAllData();
      if (menuTemplates.length === 0) {
        getTemplates().then(res => {
          if (res?.data && Array.isArray(res.data)) setMenuTemplates(res.data);
        }).catch(() => setMenuTemplates([]));
      }
    }
  }, [restaurantId]);

  // Combined fetch to avoid duplicate getRestaurant calls
  const fetchAllData = async () => {
    if (!restaurantId) return;
    try {
      // Fetch restaurant info and menu items in parallel
      const [restRes, menuRes] = await Promise.all([
        getRestaurant(restaurantId).catch(err => { console.error('Error fetching restaurant info:', err); return null; }),
        (async () => {
          setLoading(true);
          return getMenuItemsAll(restaurantId);
        })()
      ]);

      // Process restaurant info
      if (restRes?.data) {
        setRestaurantName(restRes.data.name);
        setRestaurantSlug(restRes.data.slug || '');
        setLoadedSlug(restRes.data.slug || '');
        setRestaurantCurrency(restRes.data.currency || { code: 'USD', symbol: '$', position: 'before' });
        setTaxConfig({
          tax_enabled: Boolean(restRes.data.tax_enabled),
          tax_rate: parseFloat(restRes.data.tax_rate) || 0,
          tax_inclusive: restRes.data.tax_inclusive == null ? true : Boolean(restRes.data.tax_inclusive),
          tax_label: restRes.data.tax_label || 'VAT',
          tax_number: restRes.data.tax_number || '',
        });
        setRequireCustomerOtp(Boolean(restRes.data.require_customer_otp));
      }

      // Process menu items
      let items = menuRes.data;

      // Initialize display_order for items that don't have it
      const itemsNeedingOrder = items.filter(item => item.display_order === null || item.display_order === undefined);
      if (itemsNeedingOrder.length > 0) {
        const sectionGroups = {};
        items.forEach(item => {
          const section = item.section || 'Other';
          if (!sectionGroups[section]) sectionGroups[section] = [];
          sectionGroups[section].push(item);
        });

        for (const section in sectionGroups) {
          const sectionItems = sectionGroups[section];
          const itemsWithOrder = sectionItems.filter(item => item.display_order != null);
          const maxOrder = itemsWithOrder.length > 0
            ? Math.max(...itemsWithOrder.map(item => item.display_order))
            : -1;

          let nextOrder = maxOrder + 1;
          for (const item of sectionItems) {
            if (item.display_order === null || item.display_order === undefined) {
              try {
                await updateMenuItem(item.id, { display_order: nextOrder });
                item.display_order = nextOrder;
                nextOrder++;
              } catch (error) {
                console.error(`Failed to initialize order for item ${item.id}:`, error);
              }
            }
          }
        }

        const updatedResponse = await getMenuItemsAll(restaurantId);
        items = updatedResponse.data;
      }

      setMenuItems(items);

      // Section order: use already-fetched restaurant data (no duplicate API call)
      if (restRes?.data) {
        let order = Array.isArray(restRes.data.section_order) ? restRes.data.section_order : [];
        const itemSections = [...new Set(items.map(item => item.section).filter(Boolean))];
        const missing = itemSections.filter(s => !order.includes(s));
        if (missing.length) {
          order = [...order, ...missing.sort()];
          setSectionOrder(order);
          await updateSectionOrder(restaurantId, order);
        } else {
          setSectionOrder(order);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCurrencies().then(res => {
      if (res?.data?.currencies) setCurrencies(res.data.currencies);
    }).catch(() => setCurrencies([]));
  }, []);

  const handleSaveRestaurantSettings = async (e) => {
    e.preventDefault();
    if (!restaurantId) return;
    setSavingSettings(true);
    try {
      const payload = {
        name: restaurantName,
        currency_code: restaurantCurrency.code,
        currency_symbol: restaurantCurrency.symbol,
        currency_position: restaurantCurrency.position,
        tax_enabled: taxConfig.tax_enabled,
        tax_rate: taxConfig.tax_rate,
        tax_inclusive: taxConfig.tax_inclusive,
        tax_label: taxConfig.tax_label,
        tax_number: taxConfig.tax_number,
        require_customer_otp: requireCustomerOtp,
      };
      // Only send `slug` when an admin actually changed it (backend ignores it
      // for non-admins anyway). Avoids re-validating an unchanged slug.
      const trimmedSlug = restaurantSlug.trim().toLowerCase();
      if (isAdmin() && trimmedSlug && trimmedSlug !== loadedSlug) {
        payload.slug = trimmedSlug;
      }
      const res = await updateRestaurant(restaurantId, payload);
      if (res?.data?.slug) {
        setRestaurantSlug(res.data.slug);
        setLoadedSlug(res.data.slug);
      }
      showSuccess(t('admin.currency.updated', { code: restaurantCurrency.code, symbol: restaurantCurrency.symbol }));
    } catch (err) {
      setResultDialogMessage(err.response?.data?.error || t('api.errors.updateRestaurant'));
      setResultDialogOpen(true);
    } finally {
      setSavingSettings(false);
    }
  };

  // Lightweight refresh: re-fetch menu items only (used after CRUD operations)
  const fetchMenuItems = async () => {
    if (!restaurantId) return;
    try {
      setLoading(true);
      const response = await getMenuItemsAll(restaurantId);
      const items = response.data;
      setMenuItems(items);

      // Merge any new sections into current sectionOrder
      const itemSections = [...new Set(items.map(item => item.section).filter(Boolean))];
      setSectionOrder(prev => {
        const missing = itemSections.filter(s => !prev.includes(s));
        return missing.length ? [...prev, ...missing.sort()] : prev;
      });
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurantChange = (e) => {
    const newRestaurantId = parseInt(e.target.value);
    setRestaurantId(newRestaurantId);
    setSectionOrder([]);
    setShowSectionOrder(false);
    const selectedRestaurant = restaurants.find(r => r.id === newRestaurantId);
    if (selectedRestaurant) {
      setRestaurantName(selectedRestaurant.name);
    }
  };

  // handleImageChange, updateTranslation, and base-lang sync
  // are now provided by useMenuItemForm and useTranslationForm hooks

  // Handle auto-translate button click
  const handleAutoTranslate = async () => {
    const baseTranslation = translations[baseLang];
    if (!baseTranslation.name) {
      setResultDialogMessage(t('translation.enterBaseFirst'));
      setResultDialogOpen(true);
      return;
    }

    const targetLangs = SUPPORTED_LANGUAGES
      .map(l => l.code)
      .filter(code => code !== baseLang);

    setTranslating(true);
    try {
      const response = await translateMenuItem({
        name: baseTranslation.name,
        description: baseTranslation.description || '',
        sourceLang: baseLang,
        targetLangs
      });

      const data = response?.data;
      if (!data || typeof data.translations !== 'object') {
        setResultDialogMessage(t('translation.failed') + ' (invalid response)');
        setResultDialogOpen(true);
        return;
      }

      const trans = data.translations;
      let anySuccess = false;
      const errors = [];

      setTranslations(prev => {
        const updated = { ...prev };
        for (const [langCode, translation] of Object.entries(trans)) {
          if (langCode === baseLang) continue;
          const name = (translation && translation.name) || '';
          const description = (translation && translation.description) || '';
          if (name || description) anySuccess = true;
          if (translation && translation.error) errors.push(`${langCode}: ${translation.error}`);
          updated[langCode] = {
            name,
            description,
            isAutoTranslated: translation && translation.isAutoTranslated
          };
        }
        return updated;
      });

      if (anySuccess) {
        showSuccess(t('translation.success'));
      } else {
        const msg = errors.length > 0
          ? t('translation.failed') + ' ' + errors.join('; ')
          : t('translation.failed') + ' ' + (data.error || t('translation.serviceUnavailable'));
        setResultDialogMessage(msg);
        setResultDialogOpen(true);
      }
    } catch (error) {
      console.error('Translation error:', error);
      const msg = error.response?.data?.error || error.message || t('translation.failed');
      setResultDialogMessage(msg);
      setResultDialogOpen(true);
    } finally {
      setTranslating(false);
    }
  };

  // resetTranslationForm is now resetTranslations from useTranslationForm hook
  const resetTranslationForm = resetTranslations;

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    // Get the base language name for validation
    const baseTranslation = translations[baseLang];
    if (!baseTranslation.name || !itemPrice) {
      setResultDialogMessage(t('admin.addMenuItem.validation.nameAndPrice'));
      setResultDialogOpen(true);
      return;
    }

    try {
      // Get the next display_order for this section
      const sectionItems = menuItems.filter(item => (item.section || 'Other') === (itemSection || 'Other'));
      const maxOrder = sectionItems.length > 0 
        ? Math.max(...sectionItems.map(item => item.display_order || 0))
        : -1;
      
      // Use base language for the main item
      const itemData = {
        name: baseTranslation.name,
        description: baseTranslation.description || '',
        price: parseFloat(itemPrice),
        section: itemSection || null,
        category: itemCategory || null,
        display_order: maxOrder + 1,
        imageData: imagePreview
      };

      const response = await addMenuItem(restaurantId, itemData);
      const newItemId = response.data?.id;
      
      // Save translations if we have any non-empty ones
      if (newItemId) {
        const translationsToSave = {};
        for (const [langCode, trans] of Object.entries(translations)) {
          if (trans.name) {
            translationsToSave[langCode] = trans;
          }
        }
        if (Object.keys(translationsToSave).length > 0) {
          try {
            await saveMenuItemTranslations(newItemId, translationsToSave);
          } catch (transError) {
            console.error('Error saving translations:', transError);
            // Don't fail the whole operation, just log the error
          }
        }
      }
      
      // If new section, add it to the order
      if (itemSection && !sectionOrder.includes(itemSection)) {
        const newOrder = [...sectionOrder, itemSection];
        setSectionOrder(newOrder);
        try { await updateSectionOrder(restaurantId, newOrder); } catch (e) { console.error(e); }
      }
      
      // Reset form
      resetAddForm();
      resetTranslationForm();
      
      // Refresh menu items
      await fetchMenuItems();
      showSuccess(t('admin.addMenuItem.successMessage'));
    } catch (error) {
      console.error('Error adding menu item:', error);
      setResultDialogMessage(error.response?.data?.error || t('api.errors.addMenuItem'));
      setResultDialogOpen(true);
    }
  };

  // Bulk translate all menu items
  const handleBulkTranslate = async () => {
    const targetLangs = SUPPORTED_LANGUAGES
      .map(l => l.code)
      .filter(code => code !== baseLang);

    setBulkTranslating(true);
    setBulkProgress(0);
    setBulkResults(null);

    try {
      const response = await translateAllMenu(restaurantId, baseLang, targetLangs);
      setBulkResults(response.data);
      setBulkProgress(100);
      
      // Refresh menu items to show new translation status
      await fetchMenuItems();
      showSuccess(t('translation.bulkSuccess', { count: response.data.translated }));
    } catch (error) {
      console.error('Bulk translation error:', error);
      setResultDialogMessage(error.response?.data?.error || t('translation.bulkFailed'));
      setResultDialogOpen(true);
    } finally {
      setBulkTranslating(false);
    }
  };

  const handleEditItem = async (item) => {
    populateEditForm(item);
    // Override preview with full URL for display
    setEditImagePreview(item.image_url ? getImageUrl(item.image_url) : null);

    // Fetch existing translations so the language tabs reflect the saved state.
    try {
      const response = await getMenuItemTranslations(item.id);
      setEditTranslationsFromArray(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch translations for edit:', error);
    }
  };

  const handleCancelEdit = () => {
    resetEditForm();
  };

  const handleEditAutoTranslate = async () => {
    if (!editItemName) {
      setResultDialogMessage(t('translation.enterBaseFirst'));
      setResultDialogOpen(true);
      return;
    }

    const targetLangs = SUPPORTED_LANGUAGES
      .map(l => l.code)
      .filter(code => code !== 'en');

    setEditTranslating(true);
    try {
      const response = await translateMenuItem({
        name: editItemName,
        description: editItemDescription || '',
        sourceLang: 'en',
        targetLangs,
      });

      const data = response?.data;
      if (!data || typeof data.translations !== 'object') {
        setResultDialogMessage(t('translation.failed') + ' (invalid response)');
        setResultDialogOpen(true);
        return;
      }

      const trans = data.translations;
      let anySuccess = false;
      const errors = [];
      const updates = {};
      for (const [langCode, translation] of Object.entries(trans)) {
        if (langCode === 'en') continue;
        if (translation && translation.error) {
          errors.push(`${langCode}: ${translation.error}`);
          continue;
        }
        const name = (translation && translation.name) || '';
        const description = (translation && translation.description) || '';
        if (name || description) {
          anySuccess = true;
          updates[langCode] = { name, description, isAutoTranslated: true };
        }
      }

      if (Object.keys(updates).length > 0) {
        setEditTranslations(prev => ({ ...prev, ...updates }));
      }

      if (anySuccess) {
        showSuccess(t('translation.success'));
      } else if (errors.some(e => e.includes('API_KEY_REQUIRED'))) {
        setResultDialogMessage(t('translation.apiKeyRequired'));
        setResultDialogOpen(true);
      } else {
        const msg = errors.length > 0
          ? t('translation.failed') + ' ' + errors.join('; ')
          : t('translation.failed') + ' ' + (data.error || t('translation.serviceUnavailable'));
        setResultDialogMessage(msg);
        setResultDialogOpen(true);
      }
    } catch (error) {
      console.error('Auto-translate error:', error);
      const msg = error.response?.data?.error || error.message || t('translation.failed');
      setResultDialogMessage(msg);
      setResultDialogOpen(true);
    } finally {
      setEditTranslating(false);
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();

    if (!editItemName || !editItemPrice) {
      setResultDialogMessage(t('admin.addMenuItem.validation.nameAndPrice'));
      setResultDialogOpen(true);
      return;
    }

    try {
      const updateData = {
        name: editItemName,
        description: editItemDescription,
        price: parseFloat(editItemPrice),
        section: editItemSection || null,
        category: editItemCategory || null
      };

      // If new image is selected, include it
      if (editImagePreview && editImagePreview !== getImageUrl(editingItem.image_url)) {
        updateData.imageData = editImagePreview;
      }

      const itemId = editingItem.id;
      await updateMenuItem(itemId, updateData);

      // Persist non-empty es/ar translations. The English row is implied by menu_items.name.
      const translationsToSave = {};
      for (const [langCode, trans] of Object.entries(editTranslations)) {
        if (langCode === 'en') continue;
        if (trans.name) translationsToSave[langCode] = trans;
      }
      if (Object.keys(translationsToSave).length > 0) {
        try {
          await saveMenuItemTranslations(itemId, translationsToSave);
        } catch (transError) {
          console.error('Error saving translations:', transError);
          showSuccess(t('translation.failed'));
        }
      }

      // If section changed, update section order and save to API
      if (editItemSection && editItemSection !== editingItem.section && !sectionOrder.includes(editItemSection)) {
        const newOrder = [...sectionOrder, editItemSection];
        setSectionOrder(newOrder);
        try { await updateSectionOrder(restaurantId, newOrder); } catch (e) { console.error(e); }
      }

      handleCancelEdit();
      await fetchMenuItems();
      showSuccess(t('admin.editMenuItem.successMessage'));
    } catch (error) {
      console.error('Error updating menu item:', error);
      setResultDialogMessage(error.response?.data?.error || t('api.errors.updateMenuItem'));
      setResultDialogOpen(true);
    }
  };

  const handleDeleteItemClick = (id) => {
    setDeleteItemConfirmId(id);
    setDeleteItemConfirmOpen(true);
  };

  const handleDeleteItemConfirm = async () => {
    if (deleteItemConfirmId == null) return;
    const id = deleteItemConfirmId;
    setDeleteItemConfirmOpen(false);
    setDeleteItemConfirmId(null);
    try {
      await deleteMenuItem(id);
      await fetchMenuItems();
      showSuccess(t('admin.deleteMenuItem.successMessage'));
    } catch (error) {
      console.error('Error deleting menu item:', error);
      setResultDialogMessage(error.response?.data?.error || t('api.errors.deleteMenuItem'));
      setResultDialogOpen(true);
    }
  };

  const handleMoveSectionUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...sectionOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setSectionOrder(newOrder);
    try {
      await updateSectionOrder(restaurantId, newOrder);
      showSuccess(t('admin.sections.orderUpdated'));
    } catch (error) {
      console.error('Error saving section order:', error);
      setResultDialogMessage(t('api.errors.updateRestaurant'));
      setResultDialogOpen(true);
    }
  };

  const handleMoveSectionDown = async (index) => {
    if (index === sectionOrder.length - 1) return;
    const newOrder = [...sectionOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setSectionOrder(newOrder);
    try {
      await updateSectionOrder(restaurantId, newOrder);
      showSuccess(t('admin.sections.orderUpdated'));
    } catch (error) {
      console.error('Error saving section order:', error);
      setResultDialogMessage(t('api.errors.updateRestaurant'));
      setResultDialogOpen(true);
    }
  };

  const handleAddSection = async () => {
    const name = (newSectionName || '').trim();
    if (!name) {
      setResultDialogMessage(t('admin.renameSection.validation.required'));
      setResultDialogOpen(true);
      return;
    }
    if (sectionOrder.includes(name)) {
      setResultDialogMessage(t('validation.sectionExists'));
      setResultDialogOpen(true);
      return;
    }
    const newOrder = [...sectionOrder, name];
    setSectionOrder(newOrder);
    try {
      await updateSectionOrder(restaurantId, newOrder);
      setNewSectionName('');
      showSuccess(t('admin.sections.addedSuccess', { name }));
    } catch (error) {
      console.error('Error saving section order:', error);
      setResultDialogMessage(t('api.errors.updateRestaurant'));
      setResultDialogOpen(true);
    }
  };

  const handleRenameSectionClick = (currentName) => {
    setSectionRenameOldName(currentName);
    setSectionRenameNewName(currentName);
    setSectionRenameDialogOpen(true);
  };

  const handleRenameSectionConfirm = async () => {
    const newName = (sectionRenameNewName || '').trim();
    if (!newName) {
      setResultDialogMessage(t('admin.renameSection.validation.required'));
      setResultDialogOpen(true);
      return;
    }
    if (newName === sectionRenameOldName) {
      setSectionRenameDialogOpen(false);
      return;
    }
    try {
      await renameSection(restaurantId, sectionRenameOldName, newName);
      const newOrder = sectionOrder.map(s => s === sectionRenameOldName ? newName : s);
      setSectionOrder(newOrder);
      await updateSectionOrder(restaurantId, newOrder);
      setSectionRenameDialogOpen(false);
      setSectionRenameOldName('');
      setSectionRenameNewName('');
      await fetchMenuItems();
      showSuccess(t('admin.sections.renamedSuccess'));
    } catch (error) {
      console.error('Error renaming section:', error);
      setResultDialogMessage(error.response?.data?.error || t('api.errors.renameSection'));
      setResultDialogOpen(true);
    }
  };

  const handleDeleteSectionClick = (name) => {
    setSectionDeleteConfirmName(name);
    setSectionDeleteConfirmOpen(true);
  };

  const handleDeleteSectionConfirm = async () => {
    if (!sectionDeleteConfirmName) return;
    const name = sectionDeleteConfirmName;
    setSectionDeleteConfirmOpen(false);
    setSectionDeleteConfirmName('');
    try {
      await deleteSection(restaurantId, name);
      const newOrder = sectionOrder.filter(s => s !== name);
      setSectionOrder(newOrder);
      await updateSectionOrder(restaurantId, newOrder);
      await fetchMenuItems();
      showSuccess(t('admin.sections.deletedSuccess'));
    } catch (error) {
      console.error('Error deleting section:', error);
      setResultDialogMessage(error.response?.data?.error || t('api.errors.deleteSection'));
      setResultDialogOpen(true);
    }
  };

  const handleMoveItemUp = async (section, itemIndex, item) => {
    if (itemIndex === 0) return;
    
    const sectionItems = menuItems
      .filter(i => (i.section || 'Other') === section)
      .sort((a, b) => {
        const orderA = a.display_order ?? 999999;
        const orderB = b.display_order ?? 999999;
        if (orderA !== orderB) return orderA - orderB;
        return a.id - b.id; // Fallback to ID for consistent sorting
      });
    
    if (itemIndex >= sectionItems.length || itemIndex === 0) return;
    
    try {
      // Create new array with swapped items
      const newOrder = [...sectionItems];
      [newOrder[itemIndex - 1], newOrder[itemIndex]] = [newOrder[itemIndex], newOrder[itemIndex - 1]];
      
      // Update all items in the section with new sequential order
      const updatePromises = newOrder.map((itm, idx) => 
        updateMenuItem(itm.id, { display_order: idx })
      );
      
      await Promise.all(updatePromises);
      
      // Refresh menu items
      await fetchMenuItems();
      showSuccess(t('admin.currentMenu.orderUpdated'));
    } catch (error) {
      console.error('Error updating item order:', error);
      setResultDialogMessage(t('api.errors.updateMenuItem') + ': ' + (error.response?.data?.error || error.message));
      setResultDialogOpen(true);
    }
  };

  const handleMoveItemDown = async (section, itemIndex, item) => {
    const sectionItems = menuItems
      .filter(i => (i.section || 'Other') === section)
      .sort((a, b) => {
        const orderA = a.display_order ?? 999999;
        const orderB = b.display_order ?? 999999;
        if (orderA !== orderB) return orderA - orderB;
        return a.id - b.id; // Fallback to ID for consistent sorting
      });
    
    if (itemIndex >= sectionItems.length - 1) return;
    
    try {
      // Create new array with swapped items
      const newOrder = [...sectionItems];
      [newOrder[itemIndex], newOrder[itemIndex + 1]] = [newOrder[itemIndex + 1], newOrder[itemIndex]];
      
      // Update all items in the section with new sequential order
      const updatePromises = newOrder.map((itm, idx) => 
        updateMenuItem(itm.id, { display_order: idx })
      );
      
      await Promise.all(updatePromises);
      
      // Refresh menu items
      await fetchMenuItems();
      showSuccess(t('admin.currentMenu.orderUpdated'));
    } catch (error) {
      console.error('Error updating item order:', error);
      setResultDialogMessage(t('api.errors.updateMenuItem') + ': ' + (error.response?.data?.error || error.message));
      setResultDialogOpen(true);
    }
  };

  const showSuccess = (message) => {
    setSuccessMsg(message);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleToggleAvailability = async (item) => {
    const nextAvailable = !(item.is_available === 1 || (item.is_available !== 0 && item.is_available !== false));
    setAvailabilityTogglingId(item.id);
    try {
      const reason = nextAvailable ? null : (outOfStockReason[item.id] ?? item.out_of_stock_reason ?? '');
      await updateItemAvailability(item.id, {
        isAvailable: nextAvailable,
        reason: reason || undefined,
        reasonEs: nextAvailable ? undefined : (item.out_of_stock_reason_es || undefined),
        reasonAr: nextAvailable ? undefined : (item.out_of_stock_reason_ar || undefined),
      });
      setMenuItems(prev => prev.map(i => i.id === item.id ? { ...i, is_available: nextAvailable ? 1 : 0, out_of_stock_reason: reason || i.out_of_stock_reason } : i));
      showSuccess(nextAvailable ? t('admin.menuItem.availableNow') : t('admin.menuItem.unavailableNow'));
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.updateMenuItem'));
      setResultDialogOpen(true);
    } finally {
      setAvailabilityTogglingId(null);
    }
  };

  const handleReasonChange = (itemId, reason) => {
    setOutOfStockReason(prev => ({ ...prev, [itemId]: reason }));
  };

  const saveReasonToServer = async (itemId, reason) => {
    try {
      await updateItemAvailability(itemId, {
        isAvailable: false,
        reason: reason || undefined,
      });
      setMenuItems(prev => prev.map(i => i.id === itemId ? { ...i, out_of_stock_reason: reason } : i));
    } catch (e) {
      console.error('Failed to save out-of-stock reason', e);
    }
  };

  const openScheduleModal = async (item) => {
    setSelectedScheduleItem(item);
    setShowScheduleModal(true);
    setSchedules([]);
    setNewSchedule({ name: '', dayOfWeek: 'all', startTime: '', endTime: '' });
    setSchedulesLoading(true);
    try {
      const res = await getItemSchedules(item.id);
      setSchedules(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const fetchSchedulesForModal = async () => {
    if (!selectedScheduleItem) return;
    setSchedulesLoading(true);
    try {
      const res = await getItemSchedules(selectedScheduleItem.id);
      setSchedules(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const handleAddSchedule = async () => {
    if (!selectedScheduleItem || !newSchedule.startTime || !newSchedule.endTime) {
      setResultDialogMessage(t('admin.schedule.selectTimes', 'Please select start and end times'));
      setResultDialogOpen(true);
      return;
    }
    if (newSchedule.startTime >= newSchedule.endTime) {
      setResultDialogMessage(t('admin.schedule.endAfterStart', 'End time must be after start time'));
      setResultDialogOpen(true);
      return;
    }
    setScheduleSaving(true);
    try {
      await createItemSchedule(selectedScheduleItem.id, {
        scheduleName: newSchedule.name,
        dayOfWeek: newSchedule.dayOfWeek,
        startTime: newSchedule.startTime.length === 5 ? newSchedule.startTime + ':00' : newSchedule.startTime,
        endTime: newSchedule.endTime.length === 5 ? newSchedule.endTime + ':00' : newSchedule.endTime,
      });
      await fetchSchedulesForModal();
      setNewSchedule({ name: '', dayOfWeek: 'all', startTime: '', endTime: '' });
      showSuccess(t('admin.schedule.added', 'Schedule added'));
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.updateMenuItem'));
      setResultDialogOpen(true);
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm(t('admin.schedule.confirmDelete', 'Delete this schedule?'))) return;
    try {
      await deleteItemSchedule(scheduleId);
      await fetchSchedulesForModal();
      showSuccess(t('admin.schedule.deleted', 'Schedule deleted'));
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.updateMenuItem'));
      setResultDialogOpen(true);
    }
  };

  const handleApplyTemplate = async () => {
    if (!restaurantId || !selectedTemplateToApply) return;
    setApplyingTemplate(true);
    try {
      const res = await applyTemplate(restaurantId, selectedTemplateToApply);
      await fetchMenuItems();
      const added = res?.data?.added ?? 0;
      showSuccess(t('admin.loadFromTemplate.success', { count: added }));
      setSelectedTemplateToApply('');
    } catch (error) {
      setResultDialogMessage(error.response?.data?.error || t('api.errors.addMenuItem'));
      setResultDialogOpen(true);
    } finally {
      setApplyingTemplate(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_ORIGIN}${imageUrl}`;
  };

  const openAddonManagement = async (item) => {
    setAddonModalItem(item);
    setAddonsList([]);
    setAddonsLoading(true);
    setAddonFormOpen(false);
    setAddonFormEditing(null);
    try {
      const res = await getMenuItemAddons(item.id);
      setAddonsList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching addons:', err);
      setAddonsList([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  const closeAddonManagement = () => {
    setAddonModalItem(null);
    setAddonsList([]);
    setAddonFormOpen(false);
    setAddonFormEditing(null);
  };

  const handleSaveAddonCategory = async (data) => {
    if (!addonModalItem) return;
    if (addonFormEditing) {
      await updateAddon(addonFormEditing.id, {
        categoryName: data.categoryName,
        categoryNameEs: data.categoryNameEs,
        categoryNameAr: data.categoryNameAr,
        isRequired: data.isRequired,
        allowMultiple: data.allowMultiple
      });
      showSuccess(t('admin.addons.updated'));
    } else {
      await createAddon(addonModalItem.id, data);
      showSuccess(t('admin.addons.created'));
    }
    const res = await getMenuItemAddons(addonModalItem.id);
    setAddonsList(Array.isArray(res.data) ? res.data : []);
    setAddonFormOpen(false);
    setAddonFormEditing(null);
  };

  const handleEditAddon = (addon) => {
    setAddonFormEditing(addon);
    setAddonFormOpen(true);
  };

  const handleDeleteAddonClick = (addonId) => {
    setAddonDeleteConfirmId(addonId);
    setAddonDeleteConfirmOpen(true);
  };

  const handleDeleteAddonConfirm = async () => {
    if (addonDeleteConfirmId == null) return;
    const id = addonDeleteConfirmId;
    setAddonDeleteConfirmOpen(false);
    setAddonDeleteConfirmId(null);
    try {
      await deleteAddon(id);
      if (addonModalItem) {
        const res = await getMenuItemAddons(addonModalItem.id);
        setAddonsList(Array.isArray(res.data) ? res.data : []);
      }
      showSuccess(t('admin.addons.deleted'));
    } catch (err) {
      setResultDialogMessage(err.response?.data?.error || t('api.errors.deleteMenuItem'));
      setResultDialogOpen(true);
    }
  };

  // Unverified managers can only view the demo until admin verifies them
  if (isManager() && !isVerified()) {
    return (
      <AppShell maxWidth={false}>
        <div className="admin-inner">
          <div className="pending-verification-container">
            <div className="pending-verification-icon">⏳</div>
            <h2>{t('admin.pendingVerification.title')}</h2>
            <p>{t('admin.pendingVerification.message')}</p>
            <Link to="/menu/9?mode=demo" className="btn btn-primary">{t('admin.pendingVerification.viewDemo')}</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth={false}>
    <div className="admin-page fade-in">
      <div className="admin-inner">
        {/* ── Page Header ── */}
        <div className="admin-header">
          <div className="admin-header-left">
            <div className="admin-header-icon">🍽️</div>
            <div>
              <h1 className="admin-header-title">
                {restaurantName || t('admin.panel.title')}
              </h1>
              <p className="admin-header-meta">
                <span className="meta-badge">📦 {menuItems.length} items</span>
                <span className="meta-badge">📂 {sectionOrder.length} sections</span>
              </p>
            </div>
          </div>
          <div className="admin-header-actions">
            {restaurantId && (
              <a
                href={`${window.location.origin}/menu/${restaurantId}?table=1`}
                className="btn-header-action"
                target="_blank"
                rel="noreferrer"
              >
                👁️ {t('admin.previewMenu', 'Preview Menu')}
              </a>
            )}
            {restaurantId && (
              <Link to={`/admin/qr-codes?restaurant=${restaurantId}`} className="btn-header-action">
                📱 {t('admin.panel.qrCodes')}
              </Link>
            )}
            {isAdmin() && (
              <Link to="/admin/dashboard" className="btn-header-action">
                ← {t('nav.dashboard')}
              </Link>
            )}
            {isManager() && !isAdmin() && (
              <Link to="/manager/dashboard" className="btn-header-action">
                ← {t('nav.dashboard')}
              </Link>
            )}
          </div>
        </div>

        {/* ── Restaurant Switcher ── */}
        {restaurants.length > 1 && (
          <div className="restaurant-switcher">
            <label>🏪 {t('admin.selectRestaurant')}:</label>
            <select value={restaurantId || ''} onChange={handleRestaurantChange}>
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        )}

        {!restaurantId && restaurants.length === 0 && (
          <div className="admin-empty-state">
            <div className="admin-empty-state-icon">⚠️</div>
            <h3>{t('admin.noRestaurants')}</h3>
            <p>{t('admin.noRestaurants')}</p>
          </div>
        )}

        {/* ── Tab Navigation ── */}
        {restaurantId && (
          <div className="admin-tabs">
            <button
              type="button"
              className={`admin-tab ${activeAdminTab === 'items' ? 'active' : ''}`}
              onClick={() => setActiveAdminTab('items')}
            >
              <span className="tab-icon">📋</span>
              {t('admin.tabs.menuItems', 'Menu Items')}
              <span className="tab-count">{menuItems.length}</span>
            </button>
            <button
              type="button"
              className={`admin-tab ${activeAdminTab === 'add' ? 'active' : ''}`}
              onClick={() => setActiveAdminTab('add')}
            >
              <span className="tab-icon">➕</span>
              {t('admin.tabs.addItem', 'Add Item')}
            </button>
            <button
              type="button"
              className={`admin-tab ${activeAdminTab === 'sections' ? 'active' : ''}`}
              onClick={() => setActiveAdminTab('sections')}
            >
              <span className="tab-icon">📂</span>
              {t('admin.tabs.sections', 'Sections')}
              <span className="tab-count">{sectionOrder.length}</span>
            </button>
            <button
              type="button"
              className={`admin-tab ${activeAdminTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveAdminTab('settings')}
            >
              <span className="tab-icon">⚙️</span>
              {t('admin.tabs.settings', 'Settings')}
            </button>
          </div>
        )}

        {/* ── Tab: Menu Items ── */}
        {restaurantId && activeAdminTab === 'items' && (
          <MenuItemsList
            loading={loading}
            menuItems={menuItems}
            sectionOrder={sectionOrder}
            currency={restaurantCurrency}
            getImageUrl={getImageUrl}
            togglingId={availabilityTogglingId}
            outOfStockReason={outOfStockReason}
            onAddItem={() => setActiveAdminTab('add')}
            onOpenBulkTranslate={() => setBulkTranslateOpen(true)}
            onReasonChange={handleReasonChange}
            onSaveReason={saveReasonToServer}
            onToggleAvailability={handleToggleAvailability}
            onEditItem={handleEditItem}
            onManageAddons={openAddonManagement}
            onSchedule={openScheduleModal}
            onDeleteItem={handleDeleteItemClick}
          />
        )}

        {/* ── Tab: Add Item ── */}
        {restaurantId && activeAdminTab === 'add' && (
          <AddMenuItemForm
            itemName={itemName}
            onItemNameChange={setItemName}
            itemDescription={itemDescription}
            onItemDescriptionChange={setItemDescription}
            itemPrice={itemPrice}
            onItemPriceChange={setItemPrice}
            itemSection={itemSection}
            onItemSectionChange={setItemSection}
            itemCategory={itemCategory}
            onItemCategoryChange={setItemCategory}
            imagePreview={imagePreview}
            onImageChange={handleImageChange}
            supportedLanguages={SUPPORTED_LANGUAGES}
            baseLang={baseLang}
            onBaseLangChange={setBaseLang}
            activeLangTab={activeTab}
            onActiveLangTabChange={setActiveTab}
            translations={translations}
            onTranslationChange={updateTranslation}
            translating={translating}
            onAutoTranslate={handleAutoTranslate}
            sectionOptions={[...new Set([...sectionOrder, ...menuItems.map(i => i.section).filter(Boolean), 'Other'])]}
            showTemplateCard={menuItems.length < 3}
            menuTemplates={menuTemplates}
            selectedTemplateToApply={selectedTemplateToApply}
            onSelectTemplate={setSelectedTemplateToApply}
            applyingTemplate={applyingTemplate}
            onApplyTemplate={handleApplyTemplate}
            onOpenBulkTranslate={() => setBulkTranslateOpen(true)}
            onSubmit={handleAddItem}
            onCancel={() => setActiveAdminTab('items')}
          />
        )}

        {/* ── Tab: Sections ── */}
        {restaurantId && activeAdminTab === 'sections' && (
          <SectionsTab
            sectionOrder={sectionOrder}
            menuItems={menuItems}
            newSectionName={newSectionName}
            onNewSectionNameChange={setNewSectionName}
            onAddSection={handleAddSection}
            onMoveUp={handleMoveSectionUp}
            onMoveDown={handleMoveSectionDown}
            onRename={handleRenameSectionClick}
            onDelete={handleDeleteSectionClick}
          />
        )}

        {/* ── Tab: Settings ── */}
        {restaurantId && activeAdminTab === 'settings' && (
          <SettingsTab
            restaurantName={restaurantName}
            onRestaurantNameChange={setRestaurantName}
            restaurantSlug={restaurantSlug}
            onRestaurantSlugChange={setRestaurantSlug}
            isAdmin={isAdmin()}
            currencies={currencies}
            restaurantCurrency={restaurantCurrency}
            onCurrencyChange={setRestaurantCurrency}
            taxConfig={taxConfig}
            onTaxConfigChange={setTaxConfig}
            requireCustomerOtp={requireCustomerOtp}
            onRequireCustomerOtpChange={setRequireCustomerOtp}
            saving={savingSettings}
            onSubmit={handleSaveRestaurantSettings}
          />
        )}

        {restaurantId && (
          <>
          {/* Addon Management Modal */}
          {addonModalItem && (
            <AddonManagementModal
              item={addonModalItem}
              addons={addonsList}
              loading={addonsLoading}
              onClose={closeAddonManagement}
              onAddCategory={() => { setAddonFormEditing(null); setAddonFormOpen(true); }}
              onEdit={handleEditAddon}
              onDelete={handleDeleteAddonClick}
            />
          )}

          {addonModalItem && addonFormOpen && (
            <AddonForm
              menuItemId={addonModalItem.id}
              editAddon={addonFormEditing}
              onSave={handleSaveAddonCategory}
              onClose={() => { setAddonFormOpen(false); setAddonFormEditing(null); }}
            />
          )}

          {/* Schedule Management Modal */}
          {showScheduleModal && selectedScheduleItem && (
            <ScheduleModal
              item={selectedScheduleItem}
              schedules={schedules}
              loading={schedulesLoading}
              newSchedule={newSchedule}
              saving={scheduleSaving}
              onClose={() => setShowScheduleModal(false)}
              onNewScheduleChange={setNewSchedule}
              onAddSchedule={handleAddSchedule}
              onDeleteSchedule={handleDeleteSchedule}
            />
          )}

          {/* Edit Item Modal */}
          {editingItem && (
            <EditMenuItemModal
              name={editItemName}
              description={editItemDescription}
              price={editItemPrice}
              section={editItemSection}
              category={editItemCategory}
              imagePreview={editImagePreview}
              sectionOptions={[...new Set([...sectionOrder, ...menuItems.map(i => i.section).filter(Boolean), 'Other'])]}
              onNameChange={setEditItemName}
              onDescriptionChange={setEditItemDescription}
              onPriceChange={setEditItemPrice}
              onSectionChange={setEditItemSection}
              onCategoryChange={setEditItemCategory}
              onImageChange={handleEditImageChange}
              onRemoveImage={() => { setEditImagePreview(null); setEditItemImage(null); }}
              onSubmit={handleUpdateItem}
              onCancel={handleCancelEdit}
              supportedLanguages={SUPPORTED_LANGUAGES}
              activeLangTab={editActiveLangTab}
              onActiveLangTabChange={setEditActiveLangTab}
              translations={editTranslations}
              onTranslationChange={updateEditTranslation}
              translating={editTranslating}
              onAutoTranslate={handleEditAutoTranslate}
            />
          )}

            </>
          )}

        {/* ── Success Toast ── */}
        {successMsg && (
          <div className="success-toast" role="status" aria-live="polite">
            <span className="toast-icon">✅</span>
            <span>{successMsg}</span>
            <button type="button" className="toast-close" onClick={() => setSuccessMsg('')} aria-label="Close">✕</button>
          </div>
        )}
        </div>

      {/* Result / error message dialog */}
      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)}>
        <DialogTitle>{t('common.notice')}</DialogTitle>
        <DialogContent>{resultDialogMessage}</DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialogOpen(false)} color="primary" variant="contained">{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete menu item confirmation */}
      <Dialog open={deleteItemConfirmOpen} onClose={() => { setDeleteItemConfirmOpen(false); setDeleteItemConfirmId(null); }}>
        <DialogTitle>{t('admin.deleteMenuItem.title')}</DialogTitle>
        <DialogContent>{t('admin.deleteMenuItem.message')}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteItemConfirmOpen(false); setDeleteItemConfirmId(null); }}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteItemConfirm} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Rename section dialog */}
      <Dialog open={sectionRenameDialogOpen} onClose={() => { setSectionRenameDialogOpen(false); setSectionRenameOldName(''); setSectionRenameNewName(''); }}>
        <DialogTitle>{t('admin.renameSection.title')}</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: '0.5rem', color: 'var(--text-muted)' }}>{t('admin.renameSection.currentLabel', { name: sectionRenameOldName })}</p>
          <input
            type="text"
            className="form-input"
            placeholder={t('admin.renameSection.newNamePlaceholder')}
            value={sectionRenameNewName}
            onChange={(e) => setSectionRenameNewName(e.target.value)}
            style={{ width: '100%', marginTop: '0.5rem' }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSectionRenameDialogOpen(false); setSectionRenameOldName(''); setSectionRenameNewName(''); }}>{t('common.cancel')}</Button>
          <Button onClick={handleRenameSectionConfirm} color="primary" variant="contained">{t('admin.sections.rename')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete addon confirmation */}
      <Dialog open={addonDeleteConfirmOpen} onClose={() => { setAddonDeleteConfirmOpen(false); setAddonDeleteConfirmId(null); }}>
        <DialogTitle>{t('admin.addons.deleteTitle')}</DialogTitle>
        <DialogContent>{t('admin.addons.deleteMessage')}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddonDeleteConfirmOpen(false); setAddonDeleteConfirmId(null); }}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteAddonConfirm} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete section confirmation */}
      <Dialog open={sectionDeleteConfirmOpen} onClose={() => { setSectionDeleteConfirmOpen(false); setSectionDeleteConfirmName(''); }}>
        <DialogTitle>{t('admin.deleteSection.title')}</DialogTitle>
        <DialogContent>
          {sectionDeleteConfirmName && (
            <>
              {t('admin.deleteSection.message', { name: sectionDeleteConfirmName })}
              {menuItems.filter(item => item.section === sectionDeleteConfirmName).length > 0 && (
                <p style={{ marginTop: '0.5rem', fontWeight: 600 }}>
                  {t('admin.deleteSection.itemsCount', { count: menuItems.filter(item => item.section === sectionDeleteConfirmName).length })}
                </p>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSectionDeleteConfirmOpen(false); setSectionDeleteConfirmName(''); }}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteSectionConfirm} color="error" variant="contained">{t('admin.deleteSection.confirmButton')}</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Translate Dialog */}
      <BulkTranslateDialog
        open={bulkTranslateOpen}
        supportedLanguages={SUPPORTED_LANGUAGES}
        baseLang={baseLang}
        onBaseLangChange={setBaseLang}
        itemCount={menuItems.length}
        translating={bulkTranslating}
        results={bulkResults}
        onClose={() => {
          setBulkTranslateOpen(false);
          setBulkResults(null);
          setBulkProgress(0);
        }}
        onStart={handleBulkTranslate}
      />
    </div>
    </AppShell>
  );
};

export default Admin;
